<template>
  <div
    v-if="loading"
    align="center" 
    justify="center"
  >
    <v-progress-linear
      indeterminate
      color="primary"
    />
  </div>
  <div v-else>
    <v-card class="my-1">
      <v-card-title>Summary Listing</v-card-title>
      <v-row class="px-4">
        <v-col cols="10">
          <v-row class="px-4">
            <v-col cols="3">
              <v-menu
                v-model="startMonthMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="startMonthDisplay"
                    v-validate="'required'"
                    :error-messages="errors.collect('startMonthDisplay')" 
                    data-vv-name="startMonthDisplay"
                    label="From Payroll Period"
                    readonly
                    data-vv-as="start month"  
                    class="label-position"   
                    :class="{
                      'required':!startMonthDisplay
                    }"
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>
                        mdi-calendar
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="fromPayrollPeriod"
                  type="month"
                  :allowed-dates="allowedMonths"
                  @input="startMonthMenu = false;"
                />
              </v-menu>
            </v-col>

            <v-col cols="3">
              <v-menu
                v-model="endMonthMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="endMonthDisplay"
                    v-validate="'required'"
                    :disabled="!fromPayrollPeriod"
                    :error-messages="errors.collect('endMonthDisplay')" 
                    data-vv-name="endMonthDisplay"
                    label="To Payroll Period"
                    readonly
                    data-vv-as="end month"  
                    class="label-position"   
                    :class="{
                      'required':!endMonthDisplay
                    }"
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>
                        mdi-calendar
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="toPayrollPeriod"
                  type="month"
                  :min="fromPayrollPeriod"
                  :allowed-dates="allowedMonths"
                  @input="endMonthMenu = false;"
                />
              </v-menu>
            </v-col>

            <v-col>
              <v-autocomplete
                v-model="selectedDynamicColumns"
                label="Extra Columns"
                item-text="name"
                item-value="id"
                :items="dynamicColumns"
                clearable
                multiple
                hide-selected 
                chips
                small-chips
                deletable-chips
                :error-messages="errors.collect('selectedDynamicColumns')"
                data-vv-name="selectedDynamicColumns"  
                data-vv-as="extra columns"
              />
            </v-col>         
          </v-row>
          
          <v-row class="px-4">
            <v-col>
              <v-autocomplete
                v-model="selectedCategories"
                label="Categories"
                :error-messages="errors.collect('Categories')"
                data-vv-name="Categories"
                data-vv-as="Categories"
                :items="categories"
                clearable
                multiple
                hide-selected 
                chips
                small-chips
                deletable-chips
                item-text="name"
                item-value="id"
              />
            </v-col>
          </v-row>

          <v-row class="px-4">
            <v-col>
              <v-autocomplete
                v-model="selectedSubCategories"
                v-validate="'required'"
                label="Sub Categories"
                :error-messages="errors.collect('Sub Categories')"
                data-vv-name="Sub Categories"
                data-vv-as="Sub Categories"
                :items="filteredSubCategories"
                clearable
                multiple
                hide-selected 
                chips
                small-chips
                deletable-chips
                item-text="name"
                item-value="id"
                return-object
              />
            </v-col> 
          </v-row>

          <v-row class="px-4">
            <v-col>
              <v-autocomplete
                v-model="selectedGroups"
                :items="groupOptions"
                label="Company Groups"
                item-text="text"
                item-value="value"
                multiple
                chips
                small-chips
                deletable-chips
                hide-selected
                clearable
              />
            </v-col>
          </v-row>

          <v-row class="px-4">
            <v-col>
              <v-autocomplete
                v-model="selectedCompanies"
                v-validate="'required'"
                :items="companyOptions"
                label="Companies"
                item-text="name"
                item-value="id"
                clearable
                multiple
                chips
                small-chips
                deletable-chips
                hide-selected
                :error-messages="errors.collect('company')"
                data-vv-name="company"  
                data-vv-as="company"
              />
            </v-col>
          </v-row>
        </v-col>

        <v-col
          cols="2"
          class="ma-auto"
        >
          <v-row
            v-if="progressing" 
            class="text-center"
          >
            <v-col cols="12">
              <div>
                <span class="font-weight-bold">Downloading</span>
              </div>
              <v-progress-circular
                :rotate="360"
                :size="50"
                :width="5"
                :value="progressbar"
                color="teal"
              >
                {{ progressbar }}
              </v-progress-circular>
            </v-col>
          </v-row>

          <v-row class="text-center">
            <v-col cols="12">
              <v-btn
                class="mx-3"
                :loading="progressing"
                color="primary"
                @click="generate"
              >
                generate
              </v-btn>
            </v-col>
          </v-row> 
        </v-col>
      </v-row>
    </v-card> 
  </div>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      companies: {
        type: Array,
        required: true,
      },
      payrollPeriods: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        loading: false,
        progressing: false,
        progressbar: 0,
        categoryColumns: null,
        selectedGroups: [],
        selectedCompanies: [],
        dynamicColumns: [
          { id: 'BASED LOCATION (STATE)', name: 'BASED LOCATION (STATE)' },
          { id: 'COST CENTER', name: 'COST CENTER' },
          { id: 'DEPARTMENT', name: 'DEPARTMENT' },
          { id: 'START DATE', name: 'START DATE' },
          { id: 'POSITION', name: 'POSITION' },
          { id: 'RESIGNED DATE', name: 'RESIGNED DATE' },
        ],
        selectedDynamicColumns: [],
        fromPayrollPeriod: null,
        toPayrollPeriod: null,
        startMonthMenu: false,
        endMonthMenu: false,
        categories: [
          {
            id: 'statutories',
            name: 'Statutories',
            subCategories: [
              { id: 'basic', value: 'basic', name: 'BASIC' },
              { id: 'eeeis', value: 'eeeis', name: 'EMPLOYEE EIS' },
              { id: 'ereis', value: 'ereis', name: 'EMPLOYER EIS' },
              { id: 'eeepf', value: 'eeepf', name: 'EMPLOYEE EPF' },
              { id: 'erepf', value: 'erepf', name: 'EMPLOYER EPF' },
              { id: 'eepcb', value: 'eepcb', name: 'EMPLOYEE PCB' },
              { id: 'erpcb', value: 'erpcb', name: 'EMPLOYER PCB' },
              { id: 'eesocso', value: 'eesocso', name: 'EMPLOYEE SOCSO' },
              { id: 'ersocso', value: 'ersocso', name: 'EMPLOYER SOCSO' },
            ],
          },
          {
            id: 'earnings',
            name: 'Earnings',
            subCategories: [],
          },
          {
            id: 'deductions',
            name: 'Deductions',
            subCategories: [],
          },
        ],
        selectedCategories: [],
        selectedSubCategories: [],
      }
    },
    computed: {
      allowedMonths () {
        const payrollMonths = this.payrollPeriods.map((period) =>
          period.payroll_date.slice(0, 7)
        )

        return (date) => payrollMonths.includes(date.slice(0, 7))
      },
      startMonthDisplay () {
        return this.fromPayrollPeriod ? this.$moment(this.fromPayrollPeriod, 'YYYY-MM-DD').format('MMM YYYY') : ''
      },
      endMonthDisplay () {
        return this.toPayrollPeriod ? this.$moment(this.toPayrollPeriod, 'YYYY-MM-DD').format('MMM YYYY') : ''
      },
      sortedPeriods () {
        return [...this.payrollPeriods].sort(
          (a, b) => new Date(a.payroll_date) - new Date(b.payroll_date)
        )
      },
      currentYearPeriods () {
        const currentYear = new Date().getFullYear()

        return this.sortedPeriods.filter(p =>
          new Date(p.payroll_date).getFullYear() === currentYear
        )
      },
      allSubCategories () {
        return this.categories.flatMap(c => c.subCategories)
      },
      filteredSubCategories () {
        if (!this.selectedCategories.length) {
          return this.allSubCategories
        }
        return this.categories
          .filter(c => this.selectedCategories.includes(c.id))
          .flatMap(c => c.subCategories)
      },
      groupOptions () {
        if (!this.companies) return []
        const groups = new Set()

        this.companies.forEach(company => {
          const groupVal = company.organization_lookup_uid
          if (!groupVal) return

          groups.add(company.group)
        })

        return Array.from(groups).map(group => ({
          text: `${(group || {}).value} Group`,
          value: group.lookup_uid,
        }))
      },
      filteredCompanies () {
        if (!this.selectedGroups.length) return this.companies

        return (this.companies || []).filter(company =>
          this.selectedGroups.includes(company.organization_lookup_uid)
        )
      },
      companyOptions () {
        const companies = (this.filteredCompanies || []).map(company => ({
          name: company.name,
          id: company.id,
        }))

        return [
          { name: 'All Companies', id: 'ALL' },
          ...companies,
        ]
      },
    },
    watch: {
      selectedGroups () {
        const validIds = (this.filteredCompanies || []).map(company => company.id)

        if (this.selectedCompanies.includes('ALL')) {
          this.selectedCompanies = [...validIds]
        } else {
          this.selectedCompanies = this.selectedCompanies.filter(id =>
            validIds.includes(id)
          )
        }
      },
      selectedCompanies (newVal) {
        const last = newVal[newVal.length - 1]

        // If last selected is 'ALL', keep only 'ALL'
        if (last === 'ALL' && newVal.length > 1) {
          // Replace with only 'ALL' after current update cycle
          this.$nextTick(() => {
            this.selectedCompanies = ['ALL']
          })
        } else if (newVal.includes('ALL') && newVal.length > 1) {
          // Remove 'ALL' if other company is selected after it
          this.$nextTick(() => {
            this.selectedCompanies = newVal.filter(id => id !== 'ALL')
          })
        }
      },
      payrollPeriods: {
        immediate: true,
        handler () {
          if (this.currentYearPeriods.length > 0) {
            this.fromPayrollPeriod =
              this.currentYearPeriods[0].payroll_date.slice(0, 7)

            this.toPayrollPeriod = this.currentYearPeriods[this.currentYearPeriods.length - 1].payroll_date.slice(0, 7)
          }
        },
      },
      selectedCategories () {
        const validIds = this.filteredSubCategories.map(s => s.id)

        this.selectedSubCategories = this.selectedSubCategories.filter(id =>
          validIds.includes(id)
        )
      },
    },
    created () {
      this.loading = true
      this.loadDoes()
    },
    methods: {
      loadDoes () {
        this.$api.deductionOrEarnings().list()
          .then(res => {
            const does = _.orderBy(res.data, ['code'], ['asc'])

            const earnings = does
              .filter(d => d.is_negative === 0)
              .map(item => ({
                id: item.id,
                value: item.ea_class,
                name: item.name,
                category: 'earning',
              }))

            const deductions = does
              .filter(d => d.is_negative === 1)
              .map(item => ({
                id: item.id,
                value: item.ea_class,
                name: item.name,
                category: 'deduction',
              }))

            // inject into categories
            this.categories.find(c => c.id === 'earnings').subCategories = earnings
            this.categories.find(c => c.id === 'deductions').subCategories = deductions

            this.loading = false
          })
          .catch(error => {
            this.$snack({ 
              color: 'error',
              message: error.response.data.message, 
            })
            this.loading = false
          })
      },
      generate () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true

            this.$api.payrollReports().yearlySummaryListing(this.prepareFormData(), {
              onDownloadProgress: (progressEvent) => {
                this.progressbar = (Math.floor((progressEvent.loaded * 100) / progressEvent.total))
              },
            }).then(res => {
              let blob = new Blob([res.data], { type: res.data.type })
              let link = document.createElement('a')
              link.href = window.URL.createObjectURL(blob)
              link.setAttribute('download', 'Summary Listing ' + this.$moment(this.fromPayrollPeriod).format('MMM YYYY') + ' to ' + this.$moment(this.toPayrollPeriod).format('MMM YYYY') + ' [' + this.$moment().format('YYYY-MM-DD h.mma') + '].xlsx')
              link.click()

              this.progressing = false
              this.progressbar = 0

              this.$snack({ message: 'Report downloaded successfully' })
            }).catch(() => {
              this.progressing = false

              this.$snack({ color: 'error', message: 'Error! Please contact support' })
            })
          })
      },
      prepareFormData () {
        return {
          from: this.fromPayrollPeriod,
          to: this.toPayrollPeriod,
          company: this.selectedCompanies.includes('ALL') ? (
            this.filteredCompanies.filter(company => {
              return company.id !== 'ALL'
            }).map(company => {
              return company.id
            })
          ) : this.selectedCompanies,
          dynamicColumns: this.selectedDynamicColumns,
          selectedSubCategories: this.selectedSubCategories,
        }
      },
    },
  }
</script>
